import { fade, hex, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import type { CaptionButton, ScrollBarParts, VisualState, WindowVisualState } from "./Theme.js";

/**
 * Planetspite — the spherical-planet survival game.
 *
 * The colors are lifted from that project's own design tokens
 * (`Site/assets/site.css`), which are dark-first with a light override, so
 * this theme ships the same two variants under the same names. Token names are
 * kept in the comments below: if the site palette moves, this is the file to
 * bring back in step.
 *
 * The identity is a two-accent split, and it is worth preserving in anything
 * built on top: **ember** is action — the default button, the live rule under
 * an active window — and **mythril** is attention — focus rings, selection,
 * the focused field's edge. Nothing else is allowed to be saturated.
 */

export type PlanetspiteVariant = "dark" | "light";

/** --ember / --ember-ink / --ember-deep */
const EMBER_DARK = hex("#ff6f42");
const EMBER_DARK_HOVER = hex("#ff8659");
const EMBER_DARK_PRESSED = hex("#e0491f");
const EMBER_LIGHT = hex("#e0491f");
const EMBER_LIGHT_HOVER = hex("#b93a13");
const EMBER_LIGHT_PRESSED = hex("#98300e");

/** --mythril / --mythril-ink */
const MYTHRIL_DARK = hex("#4fc2e6");
const MYTHRIL_LIGHT = hex("#1f7fa6");

/** --sans / --mono, verbatim from the site. */
const SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const MONO = 'ui-monospace, "Cascadia Code", "SF Mono", "Segoe UI Mono", "Roboto Mono", monospace';

interface Variant {
  ember: Color;
  emberHover: Color;
  emberPressed: Color;
  emberText: Color;
  mythril: Color;
  mythrilText: Color;
  /** rgba wash used behind the active caption; --band. */
  band: Color;
  lineTwo: Color;
  inkTwo: Color;
}

const DARK: Variant = {
  ember: EMBER_DARK,
  emberHover: EMBER_DARK_HOVER,
  emberPressed: EMBER_DARK_PRESSED,
  emberText: hex("#180a04"),
  mythril: MYTHRIL_DARK,
  mythrilText: hex("#04202b"),
  band: hex("#ff6f4212"),
  lineTwo: hex("#2c3646"),
  inkTwo: hex("#aeb9c9"),
};

const LIGHT: Variant = {
  ember: EMBER_LIGHT,
  emberHover: EMBER_LIGHT_HOVER,
  emberPressed: EMBER_LIGHT_PRESSED,
  emberText: hex("#ffffff"),
  mythril: MYTHRIL_LIGHT,
  mythrilText: hex("#ffffff"),
  band: hex("#e0491f10"),
  lineTwo: hex("#c3cdda"),
  inkTwo: hex("#45566c"),
};

export class PlanetspiteTheme extends BaseTheme {
  readonly variant: PlanetspiteVariant;
  #v: Variant;

  constructor(variant: PlanetspiteVariant = "dark") {
    const dark = variant === "dark";
    const v = dark ? DARK : LIGHT;
    super({
      name: `planetspite-${variant}`,
      isDark: dark,
      metrics: makeMetrics({
        titleBarHeight: 34,
        windowBorder: 1,
        windowCornerRadius: 6,
        resizeGripSize: 6,
        captionButtonWidth: 44,
        controlCornerRadius: 4,
        controlHeight: 30,
        buttonPadding: insets(15, 6),
        buttonMinWidth: 90,
        focusRingWidth: 2,
        focusRingInset: -2,
        textBoxPadding: insets(10, 5),
        checkBoxSize: 17,
        radioSize: 17,
        checkLabelGap: 9,
        scrollBarSize: 13,
        scrollBarMinThumb: 28,
        listItemHeight: 26,
        listPadding: insets(3),
        headerHeight: 30,
        columnPadding: 9,
        dialogPadding: 18,
        dialogButtonGap: 9,
        font: { family: SANS, size: 13 },
        captionFont: { family: SANS, size: 13, weight: 600 },
        monoFont: { family: MONO, size: 12.5 },
      }),
      palette: derivePalette(
        dark
          ? {
              desktop: hex("#07090e"), // --bg
              surface: hex("#141a24"), // --card
              raised: hex("#1a2130"), // a step up from --card; the site has no lighter token
              field: hex("#0d1119"), // --plane
              ink: hex("#eef2f8"), // --ink
              accent: EMBER_DARK,
              accentText: DARK.emberText,
              border: hex("#212a38"), // --line
              titleBar: hex("#0b0e15"), // --bg-2
              titleText: hex("#eef2f8"),
              isDark: true,
              overrides: {
                windowBorderActive: DARK.lineTwo,
                windowBorderInactive: hex("#212a38"),
                controlBorderHover: DARK.lineTwo,
                textSubtle: hex("#6c778a"), // --muted
                accentHover: EMBER_DARK_HOVER,
                accentPressed: EMBER_DARK_PRESSED,
                // Attention is mythril, never ember.
                selectionBg: MYTHRIL_DARK,
                selectionText: DARK.mythrilText,
                selectionInactiveBg: hex("#23303c"),
                textSelectionBg: fade(MYTHRIL_DARK, 0.35),
                fieldBorderFocused: MYTHRIL_DARK,
                focusRing: MYTHRIL_DARK,
                scrollTrack: hex("#0b0e15"),
                scrollThumb: hex("#2c3646"),
                scrollThumbHover: hex("#3c4a5e"),
                scrollThumbPressed: hex("#4f6178"),
                headerBg: hex("#111722"),
                headerBgHover: hex("#18202c"),
                headerText: DARK.inkTwo,
                gridLine: hex("#1e2634"), // --grid
                shadow: hex("#00000073"),
              },
            }
          : {
              desktop: hex("#eef1f6"), // --bg
              surface: hex("#ffffff"), // --card
              raised: hex("#f6f8fb"), // --card-2
              field: hex("#ffffff"),
              ink: hex("#131a26"), // --ink
              accent: EMBER_LIGHT,
              accentText: LIGHT.emberText,
              border: hex("#d7deea"), // --line
              titleBar: hex("#f6f8fb"), // --card-2
              titleText: hex("#131a26"),
              isDark: false,
              overrides: {
                windowBorderActive: LIGHT.lineTwo,
                windowBorderInactive: hex("#d7deea"),
                controlBorderHover: LIGHT.lineTwo,
                textSubtle: hex("#7c8697"), // --muted
                accentHover: EMBER_LIGHT_HOVER,
                accentPressed: EMBER_LIGHT_PRESSED,
                selectionBg: MYTHRIL_LIGHT,
                selectionText: LIGHT.mythrilText,
                selectionInactiveBg: hex("#dbe3ec"),
                textSelectionBg: fade(MYTHRIL_LIGHT, 0.28),
                fieldBorderFocused: MYTHRIL_LIGHT,
                focusRing: MYTHRIL_LIGHT,
                scrollTrack: hex("#e8ecf3"), // --bg-2
                scrollThumb: hex("#c3cdda"),
                scrollThumbHover: hex("#a9b6c8"),
                scrollThumbPressed: hex("#8b9bb1"),
                headerBg: hex("#f2f5f9"),
                headerBgHover: hex("#e9eef5"),
                headerText: LIGHT.inkTwo,
                gridLine: hex("#e2e7ef"), // --grid
                shadow: hex("#131a2620"),
              },
            },
      ),
    });
    this.variant = variant;
    this.#v = v;
  }

  protected override get focusUnderline(): boolean {
    return true;
  }

  /** The ember band and rule that mark the live window. */
  protected override decorateWindow(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const b = this.metrics.windowBorder;
    const h = this.captionHeight(s);
    if (!s.active) return;
    // --band: a barely-there ember wash across the caption...
    rc.fillRect({ x: b, y: b, width: bounds.width - b * 2, height: h - 2 }, this.#v.band);
    // ...closed by a solid ember rule. This is the only ember on an idle window.
    rc.fillRect({ x: b, y: b + h - 2, width: bounds.width - b * 2, height: 2 }, this.#v.ember);
  }

  protected override captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    if (button === "close") return pressed ? this.#v.emberPressed : this.#v.ember;
    return pressed ? this.palette.controlBgPressed : this.palette.controlBgHover;
  }

  protected override closeGlyphInk(): Color {
    return this.#v.emberText;
  }

  /** Ember reads as "do the thing", so only the default button carries it. */
  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(r, s.pressed ? p.controlBgPressed : s.hovered ? p.headerBgHover : p.headerBg);
    rc.hLine(r.x, r.y + r.height - 1, r.width, s.hovered ? this.#v.mythril : p.gridLine);
    rc.vLine(r.x + r.width - 1, r.y + 5, r.height - 10, p.gridLine);
  }

  /** A slim capsule on a visible track, matching the site's scroll styling. */
  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(parts.bounds, p.scrollTrack);
    if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return;
    const hovered = parts.hoveredPart === "thumb" || s.hovered;
    const pressed = parts.pressedPart === "thumb";
    const color = pressed ? p.scrollThumbPressed : hovered ? p.scrollThumbHover : p.scrollThumb;
    const inset = 3;
    const t = parts.thumb;
    const r: Rect = parts.vertical
      ? { x: t.x + inset, y: t.y, width: Math.max(2, t.width - inset * 2), height: t.height }
      : { x: t.x, y: t.y + inset, width: t.width, height: Math.max(2, t.height - inset * 2) };
    rc.fillRect(r, color, Math.min(r.width, r.height) / 2);
  }
}
